import React, { useEffect, useState, useCallback } from 'react';
import Skeleton from '../common/Skeleton';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { isUniqueLocal } from '../../validation/uniqueness';
import Button from '../common/Button';
import Pagination from '../common/Pagination';

import { Plus, ArrowUp, ArrowDown, Minus, Search, Save, Lock } from 'lucide-react';
import riskRegisterService, { RiskRegister, RiskCategory } from '../../services/riskRegisterService';
import riskScoresService, { RiskScore } from '../../services/riskScoresService';
import RiskConfigModal from './RiskConfigModal';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import Toast, { ToastMessage } from '../common/Toast';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import WorkflowSaveBar, { SaveTarget } from '../common/WorkflowSaveBar';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';

const WF_KIND = 'rsk_register';

// Engine activity key (seeded catalogue — migration 122).
const ACT_REGISTER = 'RSK_RISK_REGISTER';

// =============================================================================
// Create modal
// =============================================================================

type CreateForm = {
    risk_code: string;
    risk_name: string;
    description?: string;
    category_id?: number;
};

const EMPTY_FORM: CreateForm = {
    risk_code: '',
    risk_name: '',
    description: '',
    category_id: undefined,
};

const RiskCreateModal = ({
    isOpen, onClose, categories, risks, onSubmit, onError, canModify, canApprove,
}: {
    isOpen: boolean;
    onClose: () => void;
    categories: RiskCategory[];
    risks: RiskRegister[];
    onSubmit: (form: CreateForm, target: SaveTarget) => Promise<void>;
    onError: (msg: string) => void;
    canModify: boolean;
    canApprove: boolean;
}) => {
    const [formData, setFormData] = useState<CreateForm>(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const { errors, validateField, clearError, validateAll, setError, reset } = useFieldValidation();

    useEffect(() => { setFormData(EMPTY_FORM); setSubmitting(false); reset(); }, [isOpen]);

    const handleSave = async (target: SaveTarget) => {
        const fieldMap: Record<string, number> = {
            risk_name: 109,
            ...(formData.description ? { description: 110 } : {}),
        };
        const valid = validateAll(
            Object.entries(fieldMap).map(([field, code]) => ({
                name: field,
                code,
                value: (formData as any)[field] ?? '',
            }))
        );
        if (!valid) return;
        if (!formData.risk_name.trim()) {
            onError('Risk name is required.');
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit(formData, target);
            onClose();
        } catch (e: any) {
            onError(extractApiError(e, 'Create failed'));
        } finally { setSubmitting(false); }
    };

    const categoryOptions = categories.map(c => ({ value: c.id, label: c.category_name }));

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>Create Risk</Modal.Header>
            <Modal.Body>
                <form id="risk-create-form" onSubmit={(e) => { e.preventDefault(); handleSave('draft'); }} className="flex flex-col gap-y-4" data-testid="MOD_risk_register_create_form">
                    <FormControl.Input
                        label="Risk Name"
                        value={formData.risk_name}
                        onChange={(e) => { setFormData({ ...formData, risk_name: e.target.value }); clearError('risk_name'); }}
                        onBlur={(e) => {
                            validateField('risk_name', 109, e.target.value);
                            if (e.target.value.trim() && !isUniqueLocal(935, e.target.value, risks)) {
                                setError('risk_name', 'A risk with this name already exists');
                            }
                        }}
                        required
                        error={errors['risk_name']}
                        data-testid="INP_risk_register_create_name"
                    />
                    <FormControl.SearchSelect
                        label="Category"
                        options={categoryOptions}
                        value={formData.category_id ?? null}
                        onChange={(v) => setFormData({ ...formData, category_id: v !== null ? Number(v) : undefined })}
                        placeholder="— Select category —"
                        clearable
                        data-testid="SEL_risk_register_create_category"
                    />
                    <FormControl.Textarea
                        label="Description"
                        value={formData.description || ''}
                        onChange={(e) => { setFormData({ ...formData, description: e.target.value }); clearError('description'); }}
                        onBlur={(e) => validateField('description', 110, e.target.value)}
                        error={errors['description']}
                        data-testid="INP_risk_register_create_description"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer>
                <WorkflowSaveBar
                    canDraft={canModify}
                    canSubmit={canModify}
                    canApprove={canApprove}
                    busy={submitting}
                    onSave={handleSave}
                />
            </Modal.Footer>
        </Modal>
    );
};

// =============================================================================
// Status pill
// =============================================================================

const StatusBadge = ({ risk }: { risk: RiskRegister }) => {
    const s = risk.status || 'DRAFT';
    const styles: Record<string, string> = {
        APPROVED: 'bg-green-100 text-green-700',
        DRAFT: 'bg-gray-100 text-gray-600',
        PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
        REJECTED: 'bg-red-100 text-red-700',
        INACTIVE: 'bg-gray-100 text-gray-500',
    };
    const label: Record<string, string> = {
        APPROVED: 'Approved',
        DRAFT: 'Draft',
        PENDING_APPROVAL: 'Pending',
        REJECTED: 'Rejected',
        INACTIVE: 'Inactive',
    };
    return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[s] || styles.DRAFT}`}>
            {label[s] || s}
        </span>
    );
};

// =============================================================================
// Main
// =============================================================================

const scoreBadgeClass = (s?: number) => {
    if (s == null) return 'bg-slate-50 text-slate-400 border-slate-200';
    if (s >= 15) return 'bg-red-50 text-red-700 border-red-200';
    if (s >= 8) return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
};

const RiskRegisterTab: React.FC = () => {
    const [risks, setRisks] = useState<RiskRegister[]>([]);
    const [categories, setCategories] = useState<RiskCategory[]>([]);
    const [latestScores, setLatestScores] = useState<Record<number, RiskScore>>({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [configRisk, setConfigRisk] = useState<RiskRegister | null>(null);
    const [workflowRisk, setWorkflowRisk] = useState<RiskRegister | null>(null);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Permission engine. Create-form buttons reflect CAPABILITY (extent), not a
    // record state, so use the extent check. The workflow modal (per existing
    // record) uses the state-aware engine.
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_REGISTER, 'view');
    const { check } = usePermissions();
    const canModify = check(ACT_REGISTER, 'view+modify').allowed;
    const canApproveExtent = check(ACT_REGISTER, 'view+modify+approve').allowed;

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [r, cs, ls] = await Promise.all([
                riskRegisterService.getAll({ search: searchTerm || undefined, status: statusFilter }),
                // Categories are a convenience dropdown — a permission/404 here must
                // NOT blank the risk list.
                riskRegisterService.getActiveCategories().catch(() => [] as RiskCategory[]),
                riskScoresService.latestForEntity().catch(() => [] as RiskScore[]),
            ]);
            setRisks(r);
            setCategories(cs);
            const map: Record<number, RiskScore> = {};
            ls.forEach(s => { map[s.risk_id] = s; });
            setLatestScores(map);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load risks') });
        } finally {
            setLoading(false);
        }
    }, [searchTerm, statusFilter]);

    useEffect(() => { if (canView) refresh(); }, [refresh, canView]);
    useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

    const handleCreateSubmit = async (form: CreateForm, target: SaveTarget) => {
        await riskRegisterService.create({
            risk_name: form.risk_name,
            description: form.description,
            category_id: form.category_id,
        }, target);
        const msg = { draft: 'Risk saved as draft.', submit: 'Risk submitted for approval.', approve: 'Risk created and approved.' }[target];
        setToast({ type: 'success', message: msg });
        await refresh();
    };

    const totalRecords = risks.length;
    const paginatedRisks = risks.slice((page - 1) * pageSize, page * pageSize);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to 'RSK_RISK_REGISTER'." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">

            {/* Header */}
            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next)}
                    noun="risk register"
                    testId="SEL_risk_register_status"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search risks…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_risk_register_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={() => setIsCreateOpen(true)} data-testid="BTN_risk_register_create">Create</Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                {/* Table */}
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        {!loading && risks.length === 0 ? (
                            <NoDataFound title="No Risks Yet" message='Click "Create" to add one.' />
                        ) : (
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Risk</th>
                                        <th className="table-list-th">Category</th>
                                        <th className="table-list-th">Controls</th>
                                        <th className="table-list-th">SOPs</th>
                                        <th className="table-list-th">Score</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'xs', 'md', 'sm', '2xs', '2xs']} />}
                                    {!loading && paginatedRisks.map((r, idx) => (
                                        <tr key={r.id} className="table-list-row" onClick={() => setConfigRisk(r)} data-testid={`BTN_risk_register_row_${r.id}_open`}>
                                            <td
                                                className="table-list-cell table-list-cell--dot"
                                                onClick={(e) => { e.stopPropagation(); setWorkflowRisk(r); }}
                                                title="Workflow — view timeline & act"
                                                data-testid={`BTN_risk_register_row_${r.id}_workflow`}
                                            >
                                                <ApprovalDot
                                                    state={(r.workflow_state || 'draft') as WorkflowState}
                                                    onClick={() => setWorkflowRisk(r)}
                                                    title="Workflow — view timeline & act"
                                                />
                                            </td>
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                {r.code
                                                    ? <span className="cell-code">{r.code}</span>
                                                    : <span className="text-muted text-xs">—</span>}
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title cell-name-lock">
                                                    {r.workflow_state === 'approved' && (
                                                        <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                    )}
                                                    {r.risk_name}
                                                </span>
                                                {r.description && (
                                                    <span className="table-list-cell-sub">{r.description}</span>
                                                )}
                                            </td>
                                            <td className="table-list-cell">{r.category_name || '—'}</td>
                                            <td className="table-list-cell">
                                                {(r.control_count ?? 0) > 0 ? (
                                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700" title={`${r.control_count} control(s) mitigate this risk`}>{r.control_count}</span>
                                                ) : <span className="text-xs text-gray-400">0</span>}
                                            </td>
                                            <td className="table-list-cell">
                                                {(r.sop_count ?? 0) > 0 ? (
                                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700" title={`${r.sop_count} SOP(s) — indirect, via controls`}>{r.sop_count}</span>
                                                ) : <span className="text-xs text-gray-400">0</span>}
                                            </td>
                                            <td className="table-list-cell">
                                                {(() => {
                                                    const ls = latestScores[r.id];
                                                    if (!ls) {
                                                        return <span className="text-xs text-slate-400 italic">Unscored</span>;
                                                    }
                                                    const delta = ls.residual_risk - ls.inherent_risk;
                                                    const TrendIcon = delta < 0 ? ArrowDown : delta > 0 ? ArrowUp : Minus;
                                                    const trendColor = delta < 0 ? 'text-emerald-600' : delta > 0 ? 'text-red-600' : 'text-slate-400';
                                                    return (
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded-md border text-sm font-bold ${scoreBadgeClass(ls.residual_risk)}`}>
                                                                {ls.residual_risk}
                                                            </span>
                                                            <TrendIcon size={14} className={trendColor} />
                                                            <span className="text-[10px] text-slate-400">
                                                                I:{ls.inherent_risk}
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        )}
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="risks"
                        />
                    </div>
                </div>
            </div>

            <RiskCreateModal
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                categories={categories}
                risks={risks}
                onSubmit={handleCreateSubmit}
                onError={(msg) => setToast({ type: 'error', message: msg })}
                canModify={canModify}
                canApprove={canApproveExtent}
            />

            {configRisk && (
                <RiskConfigModal
                    isOpen={!!configRisk}
                    onClose={() => { setConfigRisk(null); refresh(); }}
                    risk={configRisk}
                    canModify={canModify}
                    canApprove={canApproveExtent}
                />
            )}

            <WorkflowModal
                isOpen={!!workflowRisk}
                onClose={() => setWorkflowRisk(null)}
                kind={WF_KIND}
                id={workflowRisk?.id ?? null}
                onChanged={refresh}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default RiskRegisterTab;
